import React from 'react';
import { Tag, Tooltip } from 'antd';
import { ClockCircleOutlined, CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';

interface DataFreshnessIndicatorProps {
  lastUpdated?: Date | string;
  source?: string;
  staleThresholdMinutes?: number;
}

export const DataFreshnessIndicator: React.FC<DataFreshnessIndicatorProps> = ({
  lastUpdated,
  source,
  staleThresholdMinutes = 60,
}) => {
  if (!lastUpdated) {
    return (
      <Tag icon={<WarningOutlined />} color="orange">
        No timestamp
      </Tag>
    );
  }

  const updatedDate = typeof lastUpdated === 'string' ? new Date(lastUpdated) : lastUpdated;
  const now = new Date();
  const minutesAgo = Math.floor((now.getTime() - updatedDate.getTime()) / (1000 * 60));
  const hoursAgo = Math.floor(minutesAgo / 60);
  const daysAgo = Math.floor(hoursAgo / 24);

  const getStatus = () => {
    if (minutesAgo < staleThresholdMinutes) {
      return {
        color: 'success' as const,
        icon: <CheckCircleOutlined />,
        text: minutesAgo < 1 ? 'Just now' : `${minutesAgo}m ago`,
      };
    } else if (minutesAgo < 60 * 24) {
      return {
        color: 'warning' as const,
        icon: <WarningOutlined />,
        text: `${hoursAgo}h ago`,
      };
    } else {
      return {
        color: 'error' as const,
        icon: <WarningOutlined />,
        text: `${daysAgo}d ago`,
      };
    }
  };

  const status = getStatus();
  const formattedDate = updatedDate.toLocaleString();

  return (
    <Tooltip title={`Last updated: ${formattedDate}${source ? ` (Source: ${source})` : ''}`}>
      <Tag icon={status.icon} color={status.color}>
        {status.text}
      </Tag>
    </Tooltip>
  );
};

export default DataFreshnessIndicator;
